import { useContext, useState } from "react";
import { ChevronDown, Copy } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "../../trpc";
import type { RouterOutputs } from "../../trpc";
import MerchantContext from "../../contexts/MerchantContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import type { UnifiedSendItem } from "./types";

type SendMetricsModalProps = {
  item: UnifiedSendItem | null;
  onClose: () => void;
};

type FailureGroup = RouterOutputs["broadcast"]["failureBreakdown"]["groups"][number];

// "Contactos" rather than "Números": a failed send can be addressed by a
// WhatsApp id as readily as by a phone number.
const copyContacts = async (contacts: string[]) => {
  try {
    await navigator.clipboard.writeText(contacts.join("\n"));
    toast.success("Contactos copiados", {
      description: "Se copiaron al portapapeles",
      duration: 2000,
    });
  } catch {
    toast.error("No se pudo copiar", { duration: 2000 });
  }
};

const FailureGroupRow = ({ group }: { group: FailureGroup }) => {
  const [expanded, setExpanded] = useState(false);
  const hasContacts = group.contacts.length > 0;

  return (
    <div className="rounded-lg border border-gray-200">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        disabled={!hasContacts}
        className="flex w-full items-start gap-2 p-3 text-left disabled:cursor-default"
      >
        <span className="whitespace-nowrap text-xs font-medium text-gray-500">
          {group.count} ×
        </span>
        <div className="min-w-0 flex-1">
          <span className="font-mono text-sm font-semibold text-gray-900">{group.code}</span>
          {group.message && <p className="text-sm text-gray-600">{group.message}</p>}
        </div>
        {hasContacts && (
          <ChevronDown
            className={cn(
              "mt-0.5 size-4 shrink-0 text-gray-400 transition-transform",
              expanded && "rotate-180"
            )}
          />
        )}
      </button>
      {expanded && hasContacts && (
        <div className="border-t border-gray-100 p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs text-gray-500">{group.contacts.length} contactos</span>
            <button
              type="button"
              onClick={() => copyContacts(group.contacts)}
              className="inline-flex min-h-11 items-center gap-1.5 text-xs font-medium text-palomma-primary"
            >
              <Copy className="size-3.5" />
              Copiar contactos
            </button>
          </div>
          <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto">
            {group.contacts.map((contact, index) => (
              <li key={`${contact}-${index}`} className="font-mono text-sm text-gray-700">
                {contact}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

type Counter = { label: string; value: number; className: string };

const buildCounters = (item: UnifiedSendItem): Counter[] => [
  { label: "Total", value: item.totalRecipients, className: "text-gray-900" },
  { label: "Enviados", value: item.sentCount, className: "text-blue-500" },
  { label: "Entregados", value: item.deliveredCount, className: "text-green-500" },
  { label: "Respondidos", value: item.respondedCount, className: "text-purple-500" },
  { label: "Fallidos", value: item.failedCount, className: "text-red-500" },
  { label: "Omitidos", value: item.skippedOrNotSentCount, className: "text-orange-500" },
  { label: "No contactables", value: item.notContactableCount, className: "text-orange-500" },
  // Not a failure: nothing was attempted, because the uploaded contact matched
  // no lead we could address.
  { label: "No encontrados", value: item.unreachableCount, className: "text-orange-500" },
];

const SendMetricsModal = ({ item, onClose }: SendMetricsModalProps) => {
  const { merchantId } = useContext(MerchantContext);
  const input = { merchantId: merchantId ?? "", id: item?.id ?? "" };

  const { data, isLoading } = trpc.broadcast.failureBreakdown.useQuery(input, {
    enabled: !!item,
  });

  return (
    <Dialog
      open={!!item}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex h-full max-h-full flex-col md:h-auto md:max-h-[85vh] md:max-w-lg">
        {item && (
          <>
            <DialogHeader>
              <DialogTitle className="pr-6 text-left">{item.name}</DialogTitle>
            </DialogHeader>

            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
              <div className="grid grid-cols-3 gap-3">
                {buildCounters(item).map((counter) => (
                  <div
                    key={counter.label}
                    className="flex flex-col items-center rounded-lg border border-gray-200 p-3"
                  >
                    <span className={cn("text-xl font-bold", counter.className)}>
                      {counter.value}
                    </span>
                    <span className="text-xs text-gray-500">{counter.label}</span>
                  </div>
                ))}
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Detalle de fallos</h3>
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="lg" />
                  </div>
                ) : !data || data.totalFailed === 0 ? (
                  <p className="py-6 text-center text-sm text-gray-500">Sin detalle</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm font-medium text-red-500">{data.totalFailed} fallidos</p>
                    {data.groups.map((group) => (
                      <FailureGroupRow key={group.code} group={group} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SendMetricsModal;
